<script setup>
import { computed, onBeforeUnmount, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRouter } from 'vue-router';
import { useAccount } from 'dashboard/composables/useAccount';
import { useMapGetter } from 'dashboard/composables/store';
import { useCallsStore } from 'dashboard/stores/calls';
import { useRelayVoicePhoneStore } from 'dashboard/stores/relayVoicePhone';
import {
  getCallAccess,
  getCalls,
  getColleagues,
  getVoiceIdentity,
  getVoiceSession,
  matchContacts,
  postCallSummary,
} from 'dashboard/api/relayVoice';
import {
  ensureCaller,
  getMissedCallSummary,
} from 'dashboard/api/relayVoiceInbound';
import RelayVoiceClient from 'dashboard/api/channel/voice/relayVoiceClient';
import {
  callerNumber,
  cleanDialInput,
  formatPhone,
  isKeypadKey,
  parseDestination,
} from 'dashboard/helper/relayVoicePhone';
import { frontendURL, conversationUrl } from 'dashboard/helper/URLHelper';
import Button from 'dashboard/components-next/button/Button.vue';
import ProductTour from 'dashboard/components-next/tour/ProductTour.vue';
import { tourSeen } from 'dashboard/components-next/tour/tourStorage';
import ActiveCallPanel from './ActiveCallPanel.vue';
import PhoneKeypad from './PhoneKeypad.vue';
import PhoneTransferPanel from './PhoneTransferPanel.vue';
import RecentCalls from './RecentCalls.vue';

const { t } = useI18n();
const router = useRouter();
const { accountId } = useAccount();
const availability = useMapGetter('getCurrentUserAvailability');
const callsStore = useCallsStore();
const phoneStore = useRelayVoicePhoneStore();

const audio = ref(null);
const panel = ref(null);
const numberInput = ref(null);
const identity = ref(null);
const phoneIdentity = ref(null);
const connected = ref(false);
const connecting = ref(false);
const manualOffline = ref(false);
const busy = ref(false);
const error = ref('');
const tab = ref('keypad');
const number = ref('');
const destinationContact = ref(null);
const tourOpen = ref(false);

// The call: state is '' | 'incoming' | 'outgoing' | 'ongoing'.
const state = ref('');
const party = ref({});
const direction = ref('outbound');
const muted = ref(false);
const held = ref(false);
const seconds = ref(0);
const answered = ref(false);
const sentDigits = ref('');
const view = ref('call');
const colleagues = ref([]);
const colleaguesLoading = ref(false);
const transferNote = ref('');
const notice = ref('');
const recent = ref([]);
const recentLoading = ref(false);

const enabled = computed(() => phoneStore.enabled);
const open = computed({
  get: () => phoneStore.open,
  set: value => {
    phoneStore.open = value;
  },
});
const otherCall = computed(
  () => callsStore.hasActiveCall || callsStore.hasIncomingCall
);
const destination = computed(() =>
  parseDestination(number.value, {
    scope: identity.value?.outbound_scope,
    outbound: identity.value?.outbound_calling === true,
    blocked: identity.value?.blocked_prefixes || [],
  })
);
// null when calls have no time limit.
const maxMinutes = computed(() => {
  const cap = identity.value ? identity.value.max_call_seconds : 180;
  return cap ? Math.round(cap / 60) : null;
});
const destinationHint = computed(() => {
  const { kind, reason } = destination.value;
  if (kind === 'echo') return t('RELAY_VOICE.DESTINATION.ECHO');
  if (kind === 'extension') {
    return t('RELAY_VOICE.DESTINATION.EXTENSION', {
      extension: destination.value.target,
    });
  }
  if (kind === 'phone') {
    const limit = maxMinutes.value
      ? t('RELAY_VOICE.DESTINATION.LIMIT', { minutes: maxMinutes.value })
      : t('RELAY_VOICE.DESTINATION.NO_LIMIT');
    return destination.value.international
      ? t('RELAY_VOICE.DESTINATION.INTERNATIONAL', { limit })
      : t('RELAY_VOICE.DESTINATION.PHONE', { limit });
  }
  if (kind === 'invalid' && number.value.length >= 4) {
    return t(`RELAY_VOICE.DESTINATION.${reason.toUpperCase()}`);
  }
  return '';
});
const canDial = computed(
  () =>
    connected.value &&
    !state.value &&
    !otherCall.value &&
    !busy.value &&
    ['echo', 'extension', 'phone'].includes(destination.value.kind)
);
const status = computed(() => {
  if (state.value === 'incoming') return 'RINGING';
  if (state.value) return 'ON_CALL';
  if (connecting.value) return 'CONNECTING';
  return connected.value ? 'READY' : 'OFFLINE';
});
const statusDot = computed(
  () =>
    ({
      READY: 'bg-n-teal-9',
      ON_CALL: 'bg-n-amber-9',
      RINGING: 'bg-n-amber-9 animate-pulse',
      CONNECTING: 'bg-n-amber-9 animate-pulse',
    })[status.value] || 'bg-n-slate-8'
);
const missedCount = computed(
  () => recent.value.filter(call => call.outcome === 'missed').length
);
// The team's callback queue: missed calls nobody has taken, and the ones that are mine.
const callbacks = ref({ open: 0, mine: 0 });
const callbackCount = computed(
  () => callbacks.value.open + callbacks.value.mine
);
const badgeCount = computed(() => callbackCount.value || missedCount.value);
const canTransfer = computed(() => {
  const allowed = identity.value?.transfer || {};
  return direction.value === 'inbound'
    ? allowed.inbound === true
    : allowed.outbound === true;
});
const transferUnavailableNote = computed(() =>
  state.value === 'ongoing' &&
  direction.value === 'inbound' &&
  !canTransfer.value
    ? t('RELAY_VOICE.TRANSFER_INBOUND_OFF')
    : ''
);
const tourSteps = computed(() =>
  ['LAUNCHER', 'DIAL', 'IN_CALL', 'RECENT'].map(key => ({
    target: `phone-${key.toLowerCase().replace('_', '-')}`,
    title: t(`RELAY_VOICE.TOUR.PHONE.${key}.TITLE`),
    body: t(`RELAY_VOICE.TOUR.PHONE.${key}.BODY`),
  }))
);

// Ringtone: primed during a user gesture where possible, so incoming calls
// can ring while another tab is in front.
const ringtoneBlocked = ref(false);
const ringtone = new Audio('/audio/dashboard/ringtone.mp3');
ringtone.loop = true;
let ringtoneTimer;
let ringtoneRevision = 0;
const stopRingtone = () => {
  ringtoneRevision += 1;
  clearTimeout(ringtoneTimer);
  ringtone.pause();
  ringtone.currentTime = 0;
};
const playRingtone = () => {
  ringtoneRevision += 1;
  const revision = ringtoneRevision;
  ringtone.volume = 1;
  return ringtone.play().then(
    () => {
      if (revision === ringtoneRevision) ringtoneBlocked.value = false;
    },
    () => {
      if (revision === ringtoneRevision) ringtoneBlocked.value = true;
    }
  );
};
const testRingtone = () => {
  stopRingtone();
  playRingtone();
  ringtoneTimer = setTimeout(() => {
    if (state.value !== 'incoming') stopRingtone();
  }, 3000);
};

// A 180 Ringing response has no carrier audio. Use a local ringback until
// the carrier supplies early media (183), answers, or ends the call.
const ringback = new Audio('/audio/dashboard/ringback.wav');
ringback.loop = true;
const stopRingback = () => {
  ringback.pause();
  ringback.currentTime = 0;
};
const primeRingback = () => {
  ringback.volume = 0;
  ringback
    .play()
    .then(() => {
      if (ringback.volume === 0) stopRingback();
    })
    .catch(() => {});
};

let client;
let generation = 0;
let connectionGeneration = 0;
let timer;
const stopTimer = () => clearInterval(timer);

watch(
  state,
  value => {
    callsStore.relayVoiceBusy = Boolean(value);
    phoneStore.inCall = Boolean(value);
    if (value === 'incoming') playRingtone();
    else stopRingtone();
    if (value !== 'outgoing') stopRingback();
    stopTimer();
    if (value === 'ongoing') {
      answered.value = true;
      timer = setInterval(() => {
        seconds.value += 1;
      }, 1000);
    }
  },
  { flush: 'sync' }
);
watch(connected, value => {
  phoneStore.connected = value;
});

const matchToParty = match => ({
  name: match.name,
  avatarUrl: match.avatar_url,
  conversationId: match.conversation_id,
  contactId: match.contact_id,
});

const lookUp = async target => {
  if (!/^\+?\d{10,15}$/.test(target)) return null;
  try {
    const matches = await matchContacts(accountId.value, [target]);
    return matches[target] || null;
  } catch {
    return null;
  }
};

const loadCallbacks = async () => {
  if (!enabled.value) return;
  try {
    callbacks.value = await getMissedCallSummary(accountId.value);
  } catch {
    // The badge falls back to the agent's own missed calls.
  }
};
const callbackTimer = setInterval(loadCallbacks, 60000);
onBeforeUnmount(() => clearInterval(callbackTimer));

// An answered caller nobody knows yet gets a contact and a phone conversation,
// so the call is tied to Relay from the start.
const tieCaller = async () => {
  const caller = party.value.number;
  if (party.value.conversationId || !/^\+?\d{8,15}$/.test(caller || '')) return;
  try {
    const match = await ensureCaller(accountId.value, caller);
    if (state.value && party.value.number === caller)
      party.value = { ...party.value, ...matchToParty(match) };
  } catch {
    // The call goes on; the summary note links it afterwards when it can.
  }
};

const loadRecent = async () => {
  if (!enabled.value) return;
  loadCallbacks();
  recentLoading.value = true;
  try {
    recent.value = (await getCalls(accountId.value, { per_page: 20 })).data;
  } catch {
    // The phone still works without its history.
  } finally {
    recentLoading.value = false;
  }
};

const resetCall = () => {
  state.value = '';
  party.value = {};
  muted.value = false;
  held.value = false;
  seconds.value = 0;
  answered.value = false;
  sentDigits.value = '';
  view.value = 'call';
  transferNote.value = '';
};

// One private note on the customer's conversation, only for a phone number
// that matches a contact (the server checks the match).
const summarise = snapshot => {
  if (
    !phoneStore.access.call_notes ||
    !/^\+?\d{10,15}$/.test(snapshot.number || '')
  )
    return;
  let outcome = 'answered';
  if (!snapshot.answered) {
    outcome = snapshot.direction === 'inbound' ? 'missed' : 'no_answer';
  }
  postCallSummary(accountId.value, {
    number: snapshot.number,
    direction: snapshot.direction,
    outcome,
    talk_seconds: snapshot.seconds,
  }).catch(() => {});
};

const finishCall = () => {
  const snapshot = {
    number: party.value.number,
    direction: direction.value,
    answered: answered.value,
    seconds: seconds.value,
  };
  if (transferNote.value) notice.value = transferNote.value;
  resetCall();
  summarise(snapshot);
  phoneStore.callEnded();
  loadCallbacks();
};

const disconnect = async () => {
  connectionGeneration += 1;
  stopRingtone();
  const previous = client;
  client = null;
  connected.value = false;
  connecting.value = false;
  resetCall();
  if (previous) await previous.disconnect().catch(() => {});
};

const connect = async ({ userGesture = false } = {}) => {
  if (connecting.value || connected.value || !enabled.value) return;
  connectionGeneration += 1;
  const attempt = connectionGeneration;
  const revision = generation;
  const isCurrentAttempt = () =>
    revision === generation && attempt === connectionGeneration;
  connecting.value = true;
  error.value = '';
  if (userGesture) {
    // Prime the ringtone during a click so later rings may play.
    ringtone.volume = 0;
    await ringtone.play().catch(() => {});
    stopRingtone();
  }
  try {
    if (!isCurrentAttempt()) return;
    // A disconnected SIP client can still be retrying in the background.
    // Stop it before replacing it, so one tab keeps only one phone instance.
    const previous = client;
    client = null;
    if (previous) await previous.disconnect();
    if (!isCurrentAttempt()) return;
    const config = await getVoiceSession(accountId.value);
    if (!isCurrentAttempt()) return;
    phoneIdentity.value = {
      name: config.display_name,
      extension: config.username,
    };
    const phone = new RelayVoiceClient(config, audio.value, {
      onRegistered: () => {
        if (isCurrentAttempt() && client === phone) connected.value = true;
      },
      onUnregistered: () => {
        if (isCurrentAttempt() && client === phone) connected.value = false;
      },
      onServerDisconnect: () => {
        if (!isCurrentAttempt() || client !== phone) return;
        connected.value = false;
        resetCall();
      },
      onCallReceived: async () => {
        if (
          !isCurrentAttempt() ||
          client !== phone ||
          otherCall.value ||
          state.value
        ) {
          phone.decline().catch(() => {});
          return;
        }
        const from = callerNumber(phone.session);
        direction.value = /^1\d{3}$/.test(from) ? 'local' : 'inbound';
        party.value = {
          number: from,
          extension: direction.value === 'local' ? from : null,
        };
        state.value = 'incoming';
        open.value = true;
        const match = await lookUp(from);
        if (match && state.value)
          party.value = { ...party.value, ...matchToParty(match) };
      },
      onCallAnswered: () => {
        if (!isCurrentAttempt() || client !== phone) return;
        state.value = 'ongoing';
        if (direction.value === 'inbound') tieCaller();
      },
      onCallRinging: () => {
        if (
          !isCurrentAttempt() ||
          client !== phone ||
          state.value !== 'outgoing'
        )
          return;
        ringback.volume = 0.6;
        ringback.play().catch(() => {});
      },
      onEarlyMedia: () => {
        if (isCurrentAttempt() && client === phone) stopRingback();
      },
      onCallHangup: () => {
        if (isCurrentAttempt() && client === phone) finishCall();
      },
    });
    client = phone;
    await phone.connect();
    if (!isCurrentAttempt()) await phone.disconnect();
  } catch {
    if (isCurrentAttempt()) {
      error.value = t('RELAY_VOICE.ERROR');
      await disconnect();
    }
  } finally {
    if (isCurrentAttempt()) connecting.value = false;
  }
};

watch(
  accountId,
  async id => {
    generation += 1;
    const revision = generation;
    phoneStore.enabled = false;
    identity.value = null;
    phoneIdentity.value = null;
    recent.value = [];
    error.value = '';
    await disconnect();
    try {
      const me = await getVoiceIdentity(id);
      if (revision !== generation) return;
      identity.value = me;
      phoneStore.enabled = me.browser_calling === true;
      if (phoneStore.enabled) {
        phoneStore.access = await getCallAccess(id);
        loadRecent();
      }
    } catch {
      // Unconfigured accounts do not show a nonfunctional phone.
    }
  },
  { immediate: true }
);

// Online agents are reachable: connect while the status is Online, unless the
// agent chose Go offline in the phone. Never drop a call in progress.
watch(
  [enabled, availability],
  ([isEnabled, presence]) => {
    if (!isEnabled) return;
    if (presence === 'online' && !manualOffline.value) connect();
    if (presence === 'offline' && !state.value) disconnect();
  },
  { immediate: true }
);
watch(availability, () => {
  manualOffline.value = false;
});

const perform = async (action, message = 'ERROR') => {
  if (busy.value) return;
  busy.value = true;
  error.value = '';
  try {
    await action();
  } catch (failure) {
    error.value =
      failure?.name === 'NotAllowedError'
        ? t('RELAY_VOICE.MIC_BLOCKED')
        : t(`RELAY_VOICE.${message}`);
  } finally {
    busy.value = false;
  }
};

const dial = () => {
  if (!canDial.value) return null;
  primeRingback();
  return perform(async () => {
    const { kind, target } = destination.value;
    direction.value = kind === 'phone' ? 'outbound' : 'local';
    party.value = {
      number: target,
      extension: kind === 'extension' ? target : null,
      ...(destinationContact.value
        ? matchToParty(destinationContact.value)
        : {}),
    };
    notice.value = '';
    state.value = 'outgoing';
    try {
      await client.call(target);
    } catch (failure) {
      resetCall();
      throw failure;
    }
  }, 'CALL_FAILED');
};

const answer = () => {
  stopRingtone();
  return perform(async () => {
    try {
      await client.answer();
    } finally {
      if (state.value === 'incoming') playRingtone();
    }
  });
};
const end = () => {
  stopRingtone();
  stopRingback();
  return perform(async () => {
    try {
      await (state.value === 'incoming' ? client.decline() : client.hangup());
    } finally {
      if (state.value === 'incoming') playRingtone();
    }
  });
};
const toggleMute = () => {
  muted.value = !muted.value;
  client.mute(muted.value);
};
const toggleHold = () =>
  perform(async () => {
    await client.hold(!held.value);
    held.value = client.held;
  });
const sendDigit = key =>
  perform(async () => {
    if (!isKeypadKey(key) || state.value !== 'ongoing') return;
    await client.sendDigit(key);
    sentDigits.value = `${sentDigits.value}${key}`.slice(-24);
  });

const openTransfer = async () => {
  view.value = 'transfer';
  colleaguesLoading.value = true;
  try {
    colleagues.value = (await getColleagues(accountId.value)).filter(
      person => !person.is_self
    );
  } catch {
    colleagues.value = [];
  } finally {
    colleaguesLoading.value = false;
  }
};
const transferTo = person =>
  perform(async () => {
    transferNote.value = t('RELAY_VOICE.TRANSFER_DONE', { name: person.name });
    await client.transfer(person.extension, () => {
      transferNote.value = '';
      error.value = t('RELAY_VOICE.TRANSFER_FAILED');
    });
    view.value = 'call';
  }, 'TRANSFER_FAILED');

const openMissedCalls = () => {
  router.push({
    name: 'relay_voice_calls',
    params: { accountId: accountId.value },
    query: { view: 'missed' },
  });
};

const openConversation = () => {
  if (!party.value.conversationId) return;
  router.push(
    frontendURL(
      conversationUrl({
        accountId: accountId.value,
        id: party.value.conversationId,
      })
    )
  );
};

// Number entry: typed, pasted or from the keypad.
let lookupTimer;
watch(
  () => destination.value.target,
  target => {
    clearTimeout(lookupTimer);
    destinationContact.value = null;
    if (destination.value.kind !== 'phone') return;
    lookupTimer = setTimeout(async () => {
      const match = await lookUp(target);
      if (destination.value.target === target) destinationContact.value = match;
    }, 350);
  }
);
const onNumberInput = event => {
  number.value = cleanDialInput(event.target.value);
};
const pressKey = key => {
  if (state.value === 'ongoing') {
    sendDigit(key);
    return;
  }
  if (key === '+' && number.value) return;
  number.value = cleanDialInput(`${number.value}${key}`);
  numberInput.value?.focus();
};
const backspace = () => {
  number.value = number.value.slice(0, -1);
};
const onPanelKeydown = event => {
  if (event.key === 'Escape' && !tourOpen.value) {
    open.value = false;
    return;
  }
  const inField = ['INPUT', 'TEXTAREA', 'SELECT'].includes(
    event.target.tagName
  );
  if (inField || event.metaKey || event.ctrlKey || event.altKey) return;
  if (
    isKeypadKey(event.key) &&
    (state.value === 'ongoing' || tab.value === 'keypad')
  ) {
    event.preventDefault();
    pressKey(event.key);
  } else if (event.key === 'Backspace' && !state.value) {
    backspace();
  } else if (event.key === 'Enter' && !state.value && canDial.value) {
    dial();
  }
};
const redial = target => {
  tab.value = 'keypad';
  number.value = cleanDialInput(target);
};

watch(
  () => phoneStore.dialRequest,
  request => {
    if (!request) return;
    phoneStore.takeDialRequest();
    redial(request.number);
  }
);
watch(open, value => {
  if (!value) return;
  loadRecent();
  if (enabled.value && !tourSeen('relay-phone')) tourOpen.value = true;
});
watch(
  () => phoneStore.historyRevision,
  () => {
    if (open.value) loadRecent();
  }
);

onBeforeUnmount(() => {
  generation += 1;
  stopTimer();
  clearTimeout(lookupTimer);
  disconnect();
});
</script>

<template>
  <div
    v-if="enabled"
    class="fixed bottom-4 right-4 z-50 flex flex-col items-end gap-3"
  >
    <audio ref="audio" autoplay />
    <section
      v-show="open"
      ref="panel"
      :aria-label="t('RELAY_VOICE.TITLE')"
      class="w-[22rem] max-w-[calc(100vw-2rem)] rounded-2xl bg-n-surface-1 border border-n-weak shadow-xl flex flex-col overflow-hidden"
      @keydown="onPanelKeydown"
    >
      <header class="flex items-center gap-3 px-4 pt-4 pb-3">
        <div class="flex flex-col min-w-0 flex-1">
          <h2 class="text-sm font-semibold text-n-slate-12">
            {{ t('RELAY_VOICE.TITLE') }}
          </h2>
          <p class="text-xs text-n-slate-11 flex items-center gap-1.5 truncate">
            <span class="size-2 rounded-full shrink-0" :class="statusDot" />
            <span>{{ t(`RELAY_VOICE.STATUS.${status}`) }}</span>
            <template v-if="phoneIdentity">
              <span aria-hidden="true">·</span>
              <span>{{
                t('RELAY_VOICE.EXTENSION', {
                  extension: phoneIdentity.extension,
                })
              }}</span>
            </template>
          </p>
        </div>
        <Button
          icon="i-ph-question-bold"
          variant="ghost"
          color="slate"
          size="sm"
          :aria-label="t('RELAY_VOICE.TOUR_BUTTON')"
          @click="tourOpen = true"
        />
        <Button
          icon="i-ph-minus-bold"
          variant="ghost"
          color="slate"
          size="sm"
          :aria-label="t('RELAY_VOICE.CLOSE_PHONE')"
          @click="open = false"
        />
      </header>

      <div class="px-4 pb-4 flex flex-col gap-3">
        <ActiveCallPanel
          v-if="state && view === 'call'"
          :state="state"
          :party="party"
          :seconds="seconds"
          :muted="muted"
          :held="held"
          :digits="sentDigits"
          :can-transfer="canTransfer"
          :transfer-note="transferUnavailableNote"
          :busy="busy"
          @accept="answer"
          @decline="end"
          @end="end"
          @toggle-mute="toggleMute"
          @toggle-hold="toggleHold"
          @digit="sendDigit"
          @transfer="openTransfer"
          @open-conversation="openConversation"
        />
        <PhoneTransferPanel
          v-else-if="state && view === 'transfer'"
          :colleagues="colleagues"
          :loading="colleaguesLoading"
          :busy="busy"
          @transfer="transferTo"
          @back="view = 'call'"
        />

        <template v-else-if="!connected">
          <p class="text-sm text-n-slate-11 leading-relaxed">
            {{ t('RELAY_VOICE.OFFLINE_HINT') }}
          </p>
          <Button
            :label="t('RELAY_VOICE.CONNECT')"
            icon="i-ph-phone-bold"
            :is-loading="connecting"
            @click="
              manualOffline = false;
              connect({ userGesture: true });
            "
          />
        </template>

        <template v-else>
          <div class="flex gap-1 p-1 rounded-lg bg-n-alpha-2" role="tablist">
            <button
              v-for="name in ['keypad', 'recent']"
              :key="name"
              type="button"
              role="tab"
              :aria-selected="tab === name"
              :data-tour="name === 'recent' ? 'phone-recent' : undefined"
              class="flex-1 py-1.5 rounded-md text-xs font-medium transition-colors focus-visible:outline focus-visible:outline-2 focus-visible:outline-n-brand"
              :class="
                tab === name
                  ? 'bg-n-solid-1 text-n-slate-12 shadow-sm'
                  : 'text-n-slate-11 hover:text-n-slate-12'
              "
              @click="tab = name"
            >
              {{ t(`RELAY_VOICE.TABS.${name.toUpperCase()}`) }}
              <span
                v-if="name === 'recent' && missedCount"
                class="ms-1 px-1.5 rounded-full bg-n-ruby-9 text-white tabular-nums"
              >
                {{ missedCount }}
              </span>
            </button>
          </div>

          <div
            v-if="tab === 'keypad'"
            class="flex flex-col gap-3"
            data-tour="phone-dial"
          >
            <div class="flex flex-col items-center gap-1 min-h-[4.5rem]">
              <div class="flex items-center gap-1 w-full">
                <input
                  ref="numberInput"
                  :value="number"
                  type="tel"
                  inputmode="tel"
                  autocomplete="off"
                  :aria-label="t('RELAY_VOICE.NUMBER')"
                  :placeholder="t('RELAY_VOICE.NUMBER_PLACEHOLDER')"
                  class="flex-1 min-w-0 bg-transparent border-0 !mb-0 p-0 text-center font-display text-2xl tracking-wide tabular-nums text-n-slate-12 placeholder:text-n-slate-8 outline-none"
                  @input="onNumberInput"
                  @keydown.enter.prevent="dial"
                />
                <button
                  v-if="number"
                  type="button"
                  class="size-8 rounded-full flex items-center justify-center text-n-slate-10 hover:bg-n-alpha-2 focus-visible:outline focus-visible:outline-2 focus-visible:outline-n-brand"
                  :aria-label="t('RELAY_VOICE.BACKSPACE')"
                  @click="backspace"
                  @dblclick="number = ''"
                >
                  <span class="i-ph-backspace-bold size-5" aria-hidden="true" />
                </button>
              </div>
              <p
                v-if="destinationContact"
                class="text-sm font-medium text-n-slate-12 truncate max-w-full"
              >
                {{ destinationContact.name }}
              </p>
              <p
                v-else-if="destination.kind === 'phone'"
                class="text-xs text-n-slate-10 tabular-nums"
              >
                {{ formatPhone(destination.target) }}
              </p>
              <p
                class="text-xs text-center leading-snug"
                :class="
                  destination.kind === 'invalid'
                    ? 'text-n-ruby-11'
                    : 'text-n-slate-10'
                "
                aria-live="polite"
              >
                {{ destinationHint }}
              </p>
            </div>
            <PhoneKeypad @press="pressKey" />
            <div class="flex justify-center pt-1">
              <button
                type="button"
                class="size-14 rounded-full bg-n-teal-9 text-white flex items-center justify-center shadow-md transition-colors hover:bg-n-teal-10 disabled:opacity-40 disabled:shadow-none focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-n-teal-9"
                :disabled="!canDial"
                :aria-label="
                  destination.target
                    ? t('RELAY_VOICE.CALL_NUMBER', {
                        number: formatPhone(destination.target),
                      })
                    : t('RELAY_VOICE.CALL')
                "
                @click="dial"
              >
                <span class="i-ph-phone-fill size-6" aria-hidden="true" />
              </button>
            </div>
          </div>

          <template v-else>
            <button
              v-if="callbackCount"
              type="button"
              data-test-id="phone-callbacks"
              class="flex items-center gap-2 rounded-lg bg-n-ruby-3 px-3 py-2 text-start text-xs font-medium text-n-ruby-11 hover:bg-n-ruby-4 focus-visible:outline focus-visible:outline-2 focus-visible:outline-n-brand"
              @click="openMissedCalls"
            >
              <span
                class="i-ph-phone-x-bold size-4 shrink-0"
                aria-hidden="true"
              />
              <span class="flex-1">
                {{
                  t('RELAY_VOICE.CALLBACKS_WAITING', {
                    n: callbackCount,
                    mine: callbacks.mine,
                  })
                }}
              </span>
              <span class="i-ph-arrow-right-bold size-3.5" aria-hidden="true" />
            </button>
            <RecentCalls
              :calls="recent"
              :loading="recentLoading"
              @redial="redial"
            />
          </template>

          <div
            class="flex items-center justify-between border-t border-n-weak pt-2 -mb-1"
          >
            <Button
              :label="t('RELAY_VOICE.TEST_RINGTONE')"
              variant="link"
              color="slate"
              size="xs"
              @click="testRingtone"
            />
            <Button
              :label="t('RELAY_VOICE.DISCONNECT')"
              variant="link"
              color="slate"
              size="xs"
              @click="
                manualOffline = true;
                disconnect();
              "
            />
          </div>
        </template>

        <p v-if="notice && !state" class="text-xs text-n-teal-11" role="status">
          {{ notice }}
        </p>
        <p v-if="error" role="alert" class="text-xs text-n-ruby-11">
          {{ error }}
        </p>
        <div
          v-if="ringtoneBlocked"
          role="alert"
          class="text-xs text-n-ruby-11 flex flex-col items-start gap-1"
        >
          {{ t('RELAY_VOICE.RINGTONE_BLOCKED') }}
          <Button
            :label="t('RELAY_VOICE.ENABLE_RINGTONE')"
            variant="faded"
            color="ruby"
            size="xs"
            @click="state === 'incoming' ? playRingtone() : testRingtone()"
          />
        </div>
      </div>
    </section>

    <button
      type="button"
      data-tour="phone-launcher"
      class="relative size-14 rounded-full shadow-lg flex items-center justify-center transition-colors focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-n-brand"
      :class="
        state === 'incoming'
          ? 'bg-n-teal-9 text-white animate-pulse'
          : 'bg-n-solid-1 text-n-slate-12 border border-n-weak hover:bg-n-alpha-2'
      "
      :aria-label="
        open ? t('RELAY_VOICE.CLOSE_PHONE') : t('RELAY_VOICE.OPEN_PHONE')
      "
      :aria-expanded="open"
      @click="open = !open"
    >
      <span
        :class="state ? 'i-ph-phone-call-bold' : 'i-ph-phone-bold'"
        class="size-6"
        aria-hidden="true"
      />
      <span
        class="absolute top-1 end-1 size-3 rounded-full ring-2 ring-n-solid-1"
        :class="statusDot"
      />
      <span
        v-if="badgeCount && !state"
        data-test-id="phone-missed-badge"
        class="absolute -top-1 -start-1 min-w-5 h-5 px-1 rounded-full bg-n-ruby-9 text-white text-[0.6875rem] font-semibold flex items-center justify-center tabular-nums"
        :aria-label="t('RELAY_VOICE.MISSED_BADGE', { n: badgeCount })"
      >
        {{ badgeCount }}
      </span>
    </button>
    <ProductTour
      v-model="tourOpen"
      :steps="tourSteps"
      storage-key="relay-phone"
    />
  </div>
  <span v-else />
</template>
